/** Collision membership bits. Packed as Rapier interaction groups: membership << 16 | filter. */
export const Layer = {
  STATIC: 1 << 0,
  DYNAMIC: 1 << 1,
  PLAYER: 1 << 2,
  DINO: 1 << 3,
  HIT: 1 << 4,
  TRIGGER: 1 << 5,
  DEBRIS: 1 << 6,
  WATER: 1 << 7,
} as const;

export type LayerBit = (typeof Layer)[keyof typeof Layer];

/** Solids that block the KCC and rigid bodies. HIT / TRIGGER / WATER are query-only. */
export const SOLID =
  Layer.STATIC | Layer.DYNAMIC | Layer.PLAYER | Layer.DINO | Layer.DEBRIS;

export const ALL_LAYERS =
  Layer.STATIC |
  Layer.DYNAMIC |
  Layer.PLAYER |
  Layer.DINO |
  Layer.HIT |
  Layer.TRIGGER |
  Layer.DEBRIS |
  Layer.WATER;

/**
 * Rapier interaction groups: membership in the high 16 bits, filter in the low 16.
 * Two colliders interact iff `(a.membership & b.filter) && (b.membership & a.filter)`.
 */
export function interactionGroups(membership: number, filter: number): number {
  return ((membership & 0xffff) << 16) | (filter & 0xffff);
}

export const STATIC_GROUPS = interactionGroups(Layer.STATIC, SOLID);
export const DYNAMIC_GROUPS = interactionGroups(Layer.DYNAMIC, SOLID);
export const PLAYER_GROUPS = interactionGroups(
  Layer.PLAYER,
  Layer.STATIC | Layer.DYNAMIC | Layer.DINO | Layer.DEBRIS,
);
export const DINO_GROUPS = interactionGroups(
  Layer.DINO,
  Layer.STATIC | Layer.DYNAMIC | Layer.PLAYER | Layer.DINO | Layer.DEBRIS,
);
export const HIT_GROUPS = interactionGroups(Layer.HIT, Layer.PLAYER | Layer.DINO);
export const TRIGGER_GROUPS = interactionGroups(Layer.TRIGGER, Layer.PLAYER | Layer.DINO);
export const WATER_GROUPS = interactionGroups(Layer.WATER, Layer.PLAYER | Layer.DINO);
